import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import {
  Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, IconButton, Stack, Typography,
  TextField, InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import _ from 'lodash';
import api from '../utils/functions/api';
import { EmployeeForm } from '../components/Forms';
import * as actions from '../redux/actions';

const EmployeeEditContext = createContext(null);

export function EmployeeEditProvider({ children }) {
  const [editOpen, setEditOpen] = useState(false);
  const [editEmployeeId, setEditEmployeeId] = useState(null);

  const openEdit = useCallback((id) => {
    if (!id) return;
    setEditOpen(true);
    setEditEmployeeId(id);
  }, []);

  const closeEdit = useCallback(() => {
    setEditOpen(false);
    setEditEmployeeId(null);
  }, []);

  const ctxValue = useMemo(() => ({ openEdit }), [openEdit]);

  return (
    <EmployeeEditContext.Provider value={ctxValue}>
      {children}
      {editOpen && (
        <EmployeeEditDialog employeeId={editEmployeeId} onClose={closeEdit} />
      )}
    </EmployeeEditContext.Provider>
  );
}

function EmployeeEditDialog({ employeeId, onClose }) {
  const dispatch = useDispatch();
  const roletype = useSelector((s) => s.roletype);
  const department = useSelector((s) => s.department);

  const [editEmployeeData, setEditEmployeeData] = useState(null);
  const [editLoading, setEditLoading] = useState(true);
  const [editImage, setEditImage] = useState([]);

  // Account management state
  const [accountOpen, setAccountOpen] = useState(false);
  const [accountInfo, setAccountInfo] = useState(null);
  const [accountForm, setAccountForm] = useState({ username: '', password: '', confirmPassword: '' });
  const [showAccountPassword, setShowAccountPassword] = useState(false);

  const { control, handleSubmit, formState: { errors }, watch, setValue, reset } = useForm();

  useEffect(() => {
    setEditLoading(true);
    setEditEmployeeData(null);
    dispatch(actions.roletypeAll(''));
    dispatch(actions.departmentAll({}));
    api.get(`${process.env.REACT_APP_API_URL}/employee/${employeeId}`)
      .then(({ data }) => setEditEmployeeData(data))
      .catch(console.error)
      .finally(() => setEditLoading(false));
  }, [employeeId]);

  const refetchEmployee = async () => {
    try {
      const { data } = await api.get(`${process.env.REACT_APP_API_URL}/employee/${employeeId}`);
      setEditEmployeeData(data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleClose = () => {
    setEditImage([]);
    reset({});
    onClose();
  };

  const onSubmit = async (data) => {
    try {
      const payload = {
        ...data,
        salary: {
          month: data?.salary?.month,
          day: data?.salary?.month / 30,
          hr: data?.salary?.month / 240,
        },
      };
      if (!_.isEmpty(editImage)) {
        payload.image = { image: editImage[0]?.data_url, imageType: 'profile', alt: '' };
      }
      await dispatch(actions.employeePut(employeeId, payload));
      alert('บันทึกสำเร็จ');
      handleClose();
    } catch (error) {
      console.error(error);
    }
  };

  const handleOpenAccount = async () => {
    setAccountForm({ username: '', password: '', confirmPassword: '' });
    setAccountInfo(null);
    setShowAccountPassword(false);
    if (editEmployeeData?.uid) {
      try {
        const info = await dispatch(actions.userGetByEmployeeId(employeeId));
        setAccountInfo(info);
      } catch {
        setAccountInfo(null);
      }
    }
    setAccountOpen(true);
  };

  const handleSaveAccount = async () => {
    if (!accountForm.password) { alert('กรุณาระบุรหัสผ่าน'); return; }
    if (accountForm.password !== accountForm.confirmPassword) { alert('รหัสผ่านไม่ตรงกัน'); return; }
    try {
      if (editEmployeeData?.uid) {
        await dispatch(actions.userPut(editEmployeeData.uid?._id || editEmployeeData.uid, { password: accountForm.password }));
        alert('เปลี่ยนรหัสผ่านสำเร็จ');
      } else {
        if (!accountForm.username) { alert('กรุณาระบุ Username'); return; }
        await dispatch(actions.userLinkToEmployee(employeeId, {
          username: accountForm.username,
          password: accountForm.password,
        }));
        await refetchEmployee();
        alert('สร้างบัญชีผู้ใช้สำเร็จ');
      }
      setAccountOpen(false);
    } catch (error) {
      alert(error?.response?.data?.error?.message || 'เกิดข้อผิดพลาด');
    }
  };

  const accountDialog = (
    <Dialog open={accountOpen} onClose={() => setAccountOpen(false)} maxWidth="xs" fullWidth>
      {editEmployeeData?.uid ? (
        <>
          <DialogTitle fontWeight={700}>บัญชีผู้ใช้</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                label="Username"
                fullWidth
                size="small"
                value={accountInfo?.username || '(กำลังโหลด...)'}
                InputProps={{ readOnly: true }}
                sx={{ '& .MuiInputBase-root': { bgcolor: '#f5f5f5' } }}
              />
              <TextField
                label="รหัสผ่านใหม่"
                type={showAccountPassword ? 'text' : 'password'}
                fullWidth
                size="small"
                value={accountForm.password}
                onChange={(e) => setAccountForm((p) => ({ ...p, password: e.target.value }))}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowAccountPassword((v) => !v)}>
                        {showAccountPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="ยืนยันรหัสผ่านใหม่"
                type={showAccountPassword ? 'text' : 'password'}
                fullWidth
                size="small"
                value={accountForm.confirmPassword}
                onChange={(e) => setAccountForm((p) => ({ ...p, confirmPassword: e.target.value }))}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setAccountOpen(false)} variant="outlined">ปิด</Button>
            <Button onClick={handleSaveAccount} variant="contained">เปลี่ยนรหัสผ่าน</Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle fontWeight={700}>สร้างบัญชีผู้ใช้</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                label="Username"
                fullWidth
                size="small"
                value={accountForm.username}
                onChange={(e) => setAccountForm((p) => ({ ...p, username: e.target.value }))}
                autoComplete="off"
              />
              <TextField
                label="Password"
                type={showAccountPassword ? 'text' : 'password'}
                fullWidth
                size="small"
                value={accountForm.password}
                onChange={(e) => setAccountForm((p) => ({ ...p, password: e.target.value }))}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowAccountPassword((v) => !v)}>
                        {showAccountPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="ยืนยัน Password"
                type={showAccountPassword ? 'text' : 'password'}
                fullWidth
                size="small"
                value={accountForm.confirmPassword}
                onChange={(e) => setAccountForm((p) => ({ ...p, confirmPassword: e.target.value }))}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setAccountOpen(false)} variant="outlined">ยกเลิก</Button>
            <Button onClick={handleSaveAccount} variant="contained">สร้างบัญชี</Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );

  return (
    <>
      <Dialog
        open
        onClose={handleClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { maxHeight: '92vh' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={600}>
              แก้ไขข้อมูลพนักงาน
              {editEmployeeData?.employee_id && (
                <Chip
                  label={editEmployeeData.employee_id}
                  size="small"
                  variant="outlined"
                  sx={{ ml: 1.5, fontWeight: 400 }}
                />
              )}
            </Typography>
            <IconButton size="small" onClick={handleClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {editLoading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
              <CircularProgress />
            </Stack>
          ) : editEmployeeData ? (
            <form id="emp-edit-global-form" onSubmit={handleSubmit(onSubmit)}>
              <EmployeeForm
                key={editEmployeeData._id}
                control={control}
                Controller={Controller}
                errors={errors}
                employee={editEmployeeData}
                roletype={roletype}
                employeeImage={editImage}
                setEmployeeImage={setEditImage}
                department={department}
                watch={watch}
                setValue={setValue}
              />
            </form>
          ) : null}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
            <Button
              variant="outlined"
              color={editEmployeeData?.uid ? 'secondary' : 'inherit'}
              startIcon={<AccountCircleIcon fontSize="small" />}
              onClick={handleOpenAccount}
              size="small"
            >
              {editEmployeeData?.uid ? 'จัดการบัญชีผู้ใช้' : 'สร้าง Username'}
            </Button>
            <Chip
              label={editEmployeeData?.uid ? 'มีบัญชีแล้ว' : 'ยังไม่มีบัญชี'}
              color={editEmployeeData?.uid ? 'success' : 'default'}
              size="small"
            />
          </Stack>
          <Button variant="outlined" onClick={handleClose} startIcon={<CloseIcon />}>
            ยกเลิก
          </Button>
          <Button
            variant="contained"
            type="submit"
            form="emp-edit-global-form"
            startIcon={<SaveIcon />}
          >
            บันทึก
          </Button>
        </DialogActions>
      </Dialog>
      {accountDialog}
    </>
  );
}

export const useEmployeeEdit = () => useContext(EmployeeEditContext);
